import { Tables } from '@/types/database'
import { logger } from '@/lib/utils/logger'
import { apiFetch } from '@/lib/api/client'
import { markSignedIn, markSignedOut } from '@/lib/auth/accessToken'

type Profile = Tables<'user_profiles'>

/**
 * POST JSON to one of our /api/auth/* routes. The session lives in HttpOnly
 * cookies the server manages, so every sign-in/out step happens there; the
 * browser never runs Supabase auth (src/lib/supabase/client.ts).
 * Throws `${prefix}: <server message>` on failure – UI code matches on these.
 */
async function postAuth<T = Record<string, unknown>>(url: string, body: unknown, prefix: string): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (e) {
    throw new Error(`${prefix}: ${e instanceof Error ? e.message : String(e)}`)
  }
  let data: Record<string, unknown> = {}
  try { data = await res.json() } catch { /* empty body */ }
  if (!res.ok) {
    const message = typeof data.error === 'string' ? data.error : `HTTP ${res.status}`
    throw new Error(`${prefix}: ${message}`)
  }
  return data as T
}

class AuthService {
  /**
   * Send the sign-in email (magic link + 6-digit code). `lang` picks the
   * language of a possible follow-up "sign in with Google" email.
   * `googleMail`: the mailbox is hosted by Google (Google sign-in will work).
   */
  async sendMagicLink(email: string, userOptions?: { data?: Record<string, unknown> }, redirectTo?: string, lang?: string) {
    return postAuth<{ googleMail?: boolean }>(
      '/api/auth/otp',
      { email, options: userOptions ?? {}, redirectTo: redirectTo || '/dashboard', lang },
      'Magic link failed'
    )
  }

  /** Verify the 6-digit code; the server sets the session cookies. Callers
   * hard-navigate afterwards so the page is rendered signed in. */
  async verifyOtp(email: string, token: string, isNewUser: boolean) {
    const data = await postAuth<{ user: { id: string; email?: string } | null }>(
      '/api/auth/otp/verify',
      { email, token, isNewUser },
      'OTP verification failed'
    )
    markSignedIn()
    return data
  }

  /** Google sign-in starts on the server (it writes the PKCE verifier cookie). */
  async signInWithGoogle(redirectTo?: string) {
    window.location.assign(`/api/auth/google?${new URLSearchParams({ next: redirectTo || '/dashboard' })}`)
  }

  /**
   * Supabase defaults to scope 'global', which destroys every session the user
   * has on every device. Default to 'local' so signing out on one device is
   * just that; 'global' stays available for the explicit "sign out everywhere"
   * action on the profile page.
   */
  async signOut(scope: 'local' | 'global' = 'local') {
    await postAuth('/api/auth/signout', { scope }, 'Sign out failed')
    markSignedOut()
  }

  async checkProfileExists(email: string) {
    try {
      const response = await fetch('/api/auth/check-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to check profile')
      }

      const result = await response.json()
      // Return format: { data: { id: string } } or { data: null }
      return { data: result.exists ? { id: result.profileId } : null }
    } catch (error) {
      logger.error('[Auth Service] Unexpected error checking profile:', error)
      throw error
    }
  }

  /**
   * Own profile, read through /api/profile. The route authenticates from the
   * session cookie. `userId` is the caller's expectation and is checked
   * against the row that came back.
   */
  async getProfile(userId: string): Promise<Profile | null> {
    const { profile } = await apiFetch<{ profile: Profile | null }>('/api/profile')
    if (!profile) return null
    if (profile.id !== userId) {
      logger.warn('[Auth Service] /api/profile returned a row for another user; ignoring')
      return null
    }
    return profile
  }

  /** Start an email change; GoTrue mails both addresses. */
  async updateEmail(newEmail: string) {
    return postAuth<{ new_email: string | null }>('/api/auth/email-change', { email: newEmail }, 'Failed to initiate email change')
  }
}

export const authService = new AuthService()

/**
 * Self-service recovery when the session is corrupted (e.g. after traffic
 * spikes or stale cookie chunks): the server deletes the auth cookies – they
 * are HttpOnly, so the browser cannot – and local caches are dropped.
 * Await it before reloading.
 */
export async function clearAuthSession(): Promise<void> {
  try {
    await fetch('/api/auth/clear', { method: 'POST', credentials: 'same-origin' })
  } catch {
    // Offline: the local cleanup below still helps; the next request retries.
  }
  markSignedOut()

  // Clear localStorage session cache
  try {
    localStorage.removeItem('auth_session_cache')
    // Also clear any profile caches
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('auth_profile_cache_')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch {
    // Ignore storage errors
  }
}
